import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const channelIcons = {
  whatsapp: WhatsAppIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  telegram: TelegramIcon,
  email: EmailIcon,
  voip: PhoneInTalkIcon,
};

const channelColors = {
  whatsapp: '#25D366',
  facebook: '#1877F2',
  instagram: '#E4405F',
  telegram: '#0088CC',
  email: '#FF6B00',
  voip: '#8B5CF6',
};

export default function HubCheckout() {
  const { channel } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState(
    searchParams.get('cycle') || 'monthly'
  );

  // Contexto assinado do redirect do HUB (RedirectSignatureGuard exige tudo isso)
  const hubTenantId = searchParams.get('tenantId');
  const hubTenantName = searchParams.get('tenantName');
  const hubTs = searchParams.get('ts');
  const hubSig = searchParams.get('signature');
  const returnUrl = searchParams.get('returnUrl');

  useEffect(() => {
    loadPlan();
  }, [channel]);

  const loadPlan = async () => {
    try {
      const { data } = await api.get(`/hub-subscriptions/plans/${channel}`);
      setPlan(data);
    } catch {
      setError('Plano nao encontrado');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const handleCheckout = async () => {
    setProcessing(true);
    setError('');

    try {
      // Passa o contexto assinado no body — RedirectSignatureGuard valida
      // (mesmos nomes/tipos que o HUB usou pra assinar: tenantId/tenantName/ts/signature)
      const { data } = await api.post('/hub-subscriptions/checkout/create', {
        tenantId: hubTenantId,
        tenantName: hubTenantName || user?.name || '',
        ts: hubTs,
        signature: hubSig,
        // campos exigidos pelo DTO CreateCheckoutDto (hubTenantId é @IsNumber)
        hubTenantId: hubTenantId ? Number(hubTenantId) : 0,
        hubTenantName: hubTenantName || user?.name || '',
        channel,
        billingCycle,
        returnUrl: returnUrl || window.location.origin + '/hub/subscriptions',
        hubTenantEmail: user?.email,
      });

      // Salvar sessionId para verificação no retorno
      sessionStorage.setItem('hub_checkout_session', data.sessionId);
      if (returnUrl) {
        sessionStorage.setItem('hub_return_url', returnUrl);
      }

      if (data.paymentUrl) {
        // Redirecionar para Mercado Pago
        window.location.href = data.paymentUrl;
      } else {
        // Sem MP configurado — ir para página de retorno pendente
        navigate(
          `/hub/checkout/return?status=pending&session_id=${data.sessionId}`
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Erro ao processar checkout'
      );
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!plan) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">Plano nao encontrado para o canal: {channel}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/hub/marketplace')}
          sx={{ mt: 2 }}
        >
          Voltar ao Marketplace
        </Button>
      </Box>
    );
  }

  const Icon = channelIcons[channel] || EmailIcon;
  const color = channelColors[channel] || '#FF6B00';
  const price =
    billingCycle === 'yearly' && plan.priceYearly
      ? plan.priceYearly
      : plan.priceMonthly;
  const perMonth =
    billingCycle === 'yearly' && plan.priceYearly
      ? plan.priceYearly / 12
      : plan.priceMonthly;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      {/* Back */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/hub/marketplace')}
        sx={{ mb: 1.5 }}
      >
        Voltar ao Marketplace
      </Button>

      <Card>
        <CardContent sx={{ p: 2.5 }}>
          {/* Plan Header */}
          <Box sx={{ textAlign: 'center', mb: 1.5 }}>
            <Icon sx={{ fontSize: 40, color, mb: 0.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {plan.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {plan.description}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Billing Toggle */}
          {plan.priceYearly && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <ToggleButtonGroup
                value={billingCycle}
                exclusive
                onChange={(_, val) => val && setBillingCycle(val)}
                size="small"
              >
                <ToggleButton value="monthly">Mensal</ToggleButton>
                <ToggleButton value="yearly">
                  Anual
                  <Chip
                    label="Economize 17%"
                    size="small"
                    color="success"
                    sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Price */}
          <Box sx={{ textAlign: 'center', mb: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color }}>
              {formatPrice(perMonth)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              por mes
            </Typography>
            {billingCycle === 'yearly' && plan.priceYearly && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Total: {formatPrice(price)} cobrados anualmente
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Features */}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            Incluso no plano:
          </Typography>
          {(plan.features || []).map((feature, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 18, color: 'success.main' }}
              />
              <Typography variant="body2">{feature}</Typography>
            </Box>
          ))}

          {/* Max Connections */}
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Ate {plan.maxConnections} conexoes simultaneas
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {/* Checkout Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={
              processing ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <PaymentIcon />
              )
            }
            onClick={handleCheckout}
            disabled={processing}
            sx={{
              mt: 3,
              py: 1.5,
              bgcolor: color,
              '&:hover': { bgcolor: color, filter: 'brightness(0.9)' },
            }}
          >
            {processing ? 'Processando...' : 'Pagar com Mercado Pago'}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 2 }}
          >
            Pagamento seguro via Mercado Pago. Cancele quando quiser.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
